import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import { 
  BoltIcon,
  PlayIcon,
  StopIcon,
  SignalIcon,
  XMarkIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { useAuth, getAccessToken } from '@/lib/auth';
import { projectsApi, realtimeApi, schemaApi } from '@/lib/api';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';

interface RealtimeEvent {
  id: string;
  type: 'INSERT' | 'UPDATE' | 'DELETE';
  table: string;
  timestamp: string;
  payload: {
    new?: any;
    old?: any;
  };
}

export default function RealtimeDashboard() {
  // const queryClient = useQueryClient();
  const { id: projectId } = useParams<{ id: string }>();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [connectionStatus, setConnectionStatus] = useState<'disconnected' | 'connecting' | 'connected' | 'error'>('disconnected');
  const [events, setEvents] = useState<RealtimeEvent[]>([]);
  const [subscriptions, setSubscriptions] = useState<{ id: string; table: string; event: string; filter?: any }[]>([]);
  const [newSubscription, setNewSubscription] = useState({
    table: '',
    event: '*',
    filter: '{}'
  });
  const wsRef = useRef<WebSocket | null>(null);

  // Fetch project details
  const { data: projectResponse } = useQuery(
    ['project', projectId],
    () => projectsApi.get(projectId as string),
    {
      enabled: isAuthenticated && !!projectId,
    }
  );

  // Fetch real-time status for selected project
  const { data: statusResponse, refetch: refetchStatus } = useQuery(
    ['realtime-status', projectId],
    () => realtimeApi.getStatus(projectId as string),
    {
      enabled: isAuthenticated && !!projectId,
      refetchInterval: 5000, // Refetch every 5 seconds
    }
  );

  // Fetch project tables for subscription options
  const { data: tablesResponse } = useQuery(
    ['project-tables', projectId],
    () => schemaApi.getTables(projectId as string),
    {
      enabled: isAuthenticated && !!projectId,
    }
  );

  // Enable real-time for table mutation
  const enableRealtimeMutation = useMutation(
    ({ tableName, events }: { tableName: string; events: string[] }) => 
      realtimeApi.enableRealtime(projectId as string, tableName, events),
    {
      onSuccess: () => {
        refetchStatus();
        toast.success('Real-time enabled successfully');
      },
      onError: (_error: any) => {
        toast.error('Failed to enable real-time');
      },
    }
  );

  // Disable real-time for table mutation
  const disableRealtimeMutation = useMutation(
    (tableName: string) => 
      realtimeApi.disableRealtime(projectId as string, tableName),
    {
      onSuccess: () => {
        refetchStatus();
        toast.success('Real-time disabled successfully');
      },
      onError: (_error: any) => {
        toast.error('Failed to disable real-time');
      },
    }
  );

  // WebSocket connection functions
  const connectWebSocket = () => {
    if (wsRef.current || !projectId) return;

    setConnectionStatus('connecting');
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
    const wsUrl = apiUrl.replace('http://', 'ws://').replace('https://', 'wss://');
    const ws = new WebSocket(`${wsUrl}/realtime/v1/websocket`);
    
    ws.onopen = () => {
      setConnectionStatus('connected');
      
      // Authenticate
      const token = getAccessToken();
      ws.send(JSON.stringify({
        type: 'auth',
        token: token,
        project_id: projectId
      }));
      
      toast.success('Connected to real-time server');
    };

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        
        if (message.type === 'data_change') {
          const newEvent: RealtimeEvent = {
            id: Date.now().toString() + Math.random(),
            type: message.event,
            table: message.table,
            timestamp: new Date().toISOString(),
            payload: message.payload
          };
          
          setEvents(prev => [newEvent, ...prev.slice(0, 99)]); // Keep only last 100 events
        } else if (message.type === 'subscription_success' && message.subscription_id && message.table) {
          // Update subscription with the real ID from server
          setSubscriptions(prev => prev.map(sub => {
            // Match by table and event to find the temp subscription
            if (sub.table === message.table && sub.event === message.event && sub.id.includes('.')) {
              // The temp ID contains a decimal point from Math.random()
              return { ...sub, id: message.subscription_id };
            }
            return sub;
          }));
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };

    ws.onerror = (_error) => {
      setConnectionStatus('error');
      toast.error('WebSocket connection error');
    };

    ws.onclose = () => {
      setConnectionStatus('disconnected');
      wsRef.current = null;
      toast('Disconnected from real-time server');
    };

    wsRef.current = ws;
  };

  const disconnectWebSocket = () => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setConnectionStatus('disconnected');
    setSubscriptions([]);
    setEvents([]);
  };

  const subscribeToTable = () => {
    if (!wsRef.current || !newSubscription.table) return;

    let filter;
    try {
      filter = newSubscription.filter ? JSON.parse(newSubscription.filter) : undefined;
    } catch {
      toast.error('Invalid filter JSON');
      return;
    }

    const subscribeMessage = {
      type: 'subscribe',
      table: newSubscription.table,
      event: newSubscription.event,
      filter
    };

    // Send subscribe message
    wsRef.current.send(JSON.stringify(subscribeMessage));

    // Store a pending subscription with a temporary ID
    // We'll update it when we get the subscription_id from the server
    const tempId = Date.now().toString() + Math.random();
    const subscription = {
      id: tempId,
      table: newSubscription.table,
      event: newSubscription.event,
      filter
    };

    // Store subscription optimistically
    setSubscriptions(prev => [...prev, subscription]);
    
    const tableName = newSubscription.table;
    const eventType = newSubscription.event;

    setNewSubscription({ table: '', event: '*', filter: '{}' });
    toast.success(`Subscribed to ${tableName} ${eventType} events`);
  };

  const unsubscribeFromTable = (index: number) => {
    const subscription = subscriptions[index];
    
    if (wsRef.current) {
      wsRef.current.send(JSON.stringify({
        type: 'unsubscribe',
        subscription_id: subscription.id
      }));
    }

    setSubscriptions(prev => prev.filter((_, i) => i !== index));
    toast.success(`Unsubscribed from ${subscription.table} ${subscription.event} events`);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  if (authLoading || !isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const project = projectResponse?.data?.project;
  const status = statusResponse?.data || {};
  const tables = Array.isArray(tablesResponse?.data) ? tablesResponse.data : 
                Array.isArray(tablesResponse?.data?.tables) ? tablesResponse.data.tables :
                Array.isArray(tablesResponse?.data?.data) ? tablesResponse.data.data : [];
  
  // Get list of table names that have realtime enabled
  const enabledTableNames = new Set(
    (status.tables || []).map((t: any) => t.table_name)
  );

  const getConnectionStatusColor = () => {
    switch (connectionStatus) {
      case 'connected': return 'text-green-600 bg-green-100';
      case 'connecting': return 'text-yellow-600 bg-yellow-100';
      case 'error': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getEventTypeColor = (type: string) => {
    switch (type) {
      case 'INSERT': return 'bg-green-100 text-green-800';
      case 'UPDATE': return 'bg-blue-100 text-blue-800';
      case 'DELETE': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
      <div className="px-4 sm:px-0">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-2xl font-semibold leading-6 text-gray-900">
              Real-time Dashboard
            </h1>
            <p className="mt-2 text-sm text-gray-700">
              Monitor and manage real-time database subscriptions and events for {project?.name || 'this project'}.
            </p>
          </div>
        </div>

        {project && (
          <>
            {/* Connection Status & Controls */}
            <div className="mt-6 card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-medium text-gray-900">Connection Status</h2>
                <div className="flex items-center space-x-4">
                  <div className={`px-3 py-1 rounded-full text-sm font-medium ${getConnectionStatusColor()}`}>
                    <SignalIcon className="h-4 w-4 inline mr-1" />
                    {connectionStatus.charAt(0).toUpperCase() + connectionStatus.slice(1)}
                  </div>
                  {connectionStatus === 'disconnected' ? (
                    <button
                      onClick={connectWebSocket}
                      disabled={!projectId}
                      className="btn-primary disabled:opacity-50"
                    >
                      <PlayIcon className="h-4 w-4 mr-2" />
                      Connect
                    </button>
                  ) : (
                    <button
                      onClick={disconnectWebSocket}
                      className="btn-secondary"
                    >
                      <StopIcon className="h-4 w-4 mr-2" />
                      Disconnect
                    </button>
                  )}
                </div>
              </div>

              {/* Real-time Status Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Active Connections:</span>
                  <span className="ml-2 font-medium">{status.active_connections || 0}</span>
                </div>
                <div>
                  <span className="text-gray-500">Enabled Tables:</span>
                  <span className="ml-2 font-medium">{status.tables?.length || 0}</span>
                </div>
                <div>
                  <span className="text-gray-500">Total Events:</span>
                  <span className="ml-2 font-medium">{status.total_events || 0}</span>
                </div>
              </div>
              
              {/* Show list of enabled tables if any */}
              {status.tables && status.tables.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-500 mb-2">Tables with real-time enabled:</p>
                  <div className="flex flex-wrap gap-2">
                    {status.tables.map((table: any) => (
                      <span
                        key={table.table_name}
                        className="px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-800"
                      >
                        {table.table_name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Table Subscriptions */}
            {connectionStatus === 'connected' && (
              <div className="mt-6 card">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-medium text-gray-900">Active Subscriptions</h2>
                </div>

                {/* Add New Subscription */}
                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Subscribe to Table Events</h3>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <select
                        value={newSubscription.table}
                        onChange={(e) => setNewSubscription({ ...newSubscription, table: e.target.value })}
                        className="form-select w-full"
                      >
                        <option value="">Select table...</option>
                        {tables.map((table: any) => (
                          <option key={table.table_name} value={table.table_name}>
                            {table.table_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <select
                        value={newSubscription.event}
                        onChange={(e) => setNewSubscription({ ...newSubscription, event: e.target.value })}
                        className="form-select w-full"
                      >
                        <option value="*">All Events</option>
                        <option value="INSERT">INSERT</option>
                        <option value="UPDATE">UPDATE</option>
                        <option value="DELETE">DELETE</option>
                      </select>
                    </div>
                    <div>
                      <input
                        type="text"
                        value={newSubscription.filter}
                        onChange={(e) => setNewSubscription({ ...newSubscription, filter: e.target.value })}
                        placeholder='{"column": "value"}'
                        className="form-input w-full font-mono text-xs"
                      />
                    </div>
                    <div>
                      <button
                        onClick={subscribeToTable}
                        disabled={!newSubscription.table}
                        className="btn-primary w-full disabled:opacity-50"
                      >
                        Subscribe
                      </button>
                    </div>
                  </div>
                </div>

                {/* Current Subscriptions */}
                {subscriptions.length === 0 ? (
                  <p className="text-sm text-gray-500">No active subscriptions</p>
                ) : (
                  <div className="space-y-2">
                    {subscriptions.map((sub, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                        <div className="flex items-center space-x-4">
                          <span className="font-medium text-blue-900">{sub.table}</span>
                          <span className="text-blue-700">{sub.event}</span>
                          {sub.filter && Object.keys(sub.filter).length > 0 && (
                            <span className="text-xs font-mono text-blue-600">
                              {JSON.stringify(sub.filter)}
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => unsubscribeFromTable(index)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Real-time Events */}
            <div className="mt-6 card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-medium text-gray-900">Live Events</h2>
                <button
                  onClick={() => setEvents([])}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Clear Events
                </button>
              </div>

              {events.length === 0 ? (
                <div className="text-center py-12">
                  <BoltIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No events yet</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Connect and subscribe to tables to see real-time events.
                  </p>
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {events.map((event) => (
                    <div key={event.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-3">
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getEventTypeColor(event.type)}`}>
                            {event.type}
                          </span>
                          <span className="font-medium text-gray-900">{event.table}</span>
                          <span className="text-xs text-gray-500 flex items-center">
                            <ClockIcon className="h-3 w-3 mr-1" />
                            {formatDistanceToNow(new Date(event.timestamp), { addSuffix: true })}
                          </span>
                        </div>
                      </div>
                      
                      <div className="text-sm">
                        {event.payload.new && (
                          <div className="mb-2">
                            <span className="text-green-600 font-medium">New: </span>
                            <pre className="inline font-mono text-xs bg-green-50 px-2 py-1 rounded">
                              {JSON.stringify(event.payload.new, null, 2)}
                            </pre>
                          </div>
                        )}
                        {event.payload.old && (
                          <div>
                            <span className="text-red-600 font-medium">Old: </span>
                            <pre className="inline font-mono text-xs bg-red-50 px-2 py-1 rounded">
                              {JSON.stringify(event.payload.old, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Enable Real-time for Tables */}
            <div className="mt-6 card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-medium text-gray-900">Enable Real-time</h2>
              </div>
              
              <p className="text-sm text-gray-600 mb-4">
                Enable real-time triggers for database tables to receive live updates.
              </p>

              <div className="space-y-3">
                {tables.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500">No tables found in this database.</p>
                  </div>
                ) : (
                  tables.map((table: any) => {
                    const isEnabled = enabledTableNames.has(table.table_name);
                    const isLoading = enableRealtimeMutation.isLoading || disableRealtimeMutation.isLoading;
                    return (
                      <div key={table.table_name} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <span className="font-medium text-gray-900">{table.table_name}</span>
                          {isEnabled && (
                            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800">
                              Enabled
                            </span>
                          )}
                        </div>
                        {isEnabled ? (
                          <button
                            onClick={() => disableRealtimeMutation.mutate(table.table_name)}
                            disabled={isLoading}
                            className="btn-secondary disabled:opacity-50"
                          >
                            {disableRealtimeMutation.isLoading ? 'Disabling...' : 'Disable Real-time'}
                          </button>
                        ) : (
                          <button
                            onClick={() => enableRealtimeMutation.mutate({
                              tableName: table.table_name,
                              events: ['INSERT', 'UPDATE', 'DELETE']
                            })}
                            disabled={isLoading}
                            className="btn-primary disabled:opacity-50"
                          >
                            {enableRealtimeMutation.isLoading ? 'Enabling...' : 'Enable Real-time'}
                          </button>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </>
        )}
      </div>
  );
}